import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, BadgeDollarSign, Hammer, Clock, Sparkles, Droplets, ArrowRight, MapPin, ArrowDown } from "lucide-react";
import { IMAGES, WHY_CHOOSE, SERVICE_DETAILS, CITIES, REVIEWS } from "@/data/site";
import { usePageMeta, SectionLabel, PrimaryButton, CTABanner, StarRating } from "@/components/common";
import { Reveal, StaggerGroup, StaggerItem, MaskLines, FadeIn } from "@/components/motion";

const ICONS = { ShieldCheck, BadgeDollarSign, Hammer, Clock, Sparkles, Droplets };
const FEATURED = ["stucco-repair", "new-stucco-installation", "stucco-textures-finishes", "residential-stucco", "commercial-stucco"];

const MANIFESTO = [
  { n: "01", title: "Craftsmanship first", body: "Every wall we finish is a signature. Hand-applied textures, crisp lines, and finishes engineered to outlast Florida's harshest seasons." },
  { n: "02", title: "Honest from the estimate", body: "Transparent, competitive pricing with no surprises. We quote it straight and we stand behind every square foot." },
  { n: "03", title: "Respect for your space", body: "Reliable communication, punctual crews, and job sites left spotless. We treat your property like it's our own." },
  { n: "04", title: "Built to endure", body: "Long-lasting, weather-resistant materials and proper waterproofing — protection that holds up for decades, not seasons." },
];

const MarqueeWord = () => {
  const words = ["Stucco", "Plaster", "Craftsmanship", "Waterproofing", "Textures", "Restoration"];
  const loop = [...words, ...words];
  return (
    <section className="bg-adn-white py-16 overflow-hidden border-y border-zinc-200" data-testid="editorial-marquee">
      <div className="flex marquee-track animate-marquee" style={{ animationDuration: "45s" }}>
        {loop.map((w, i) => (
          <div key={i} className="flex items-center gap-8 px-8 shrink-0">
            <span className={`editorial-marquee text-6xl md:text-8xl ${i % 2 === 0 ? "text-adn-charcoal" : "text-stroke"}`}>{w}</span>
            <span className="h-3 w-3 rounded-full bg-adn-red shrink-0" />
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  usePageMeta("Central Florida Stucco Contractor", "A.D.N Stucco Inc — licensed & insured stucco contractor in Orlando & Central Florida. Stucco repair, installation, textures & plaster. Free estimates.");

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100vh] min-h-[680px] flex items-center overflow-hidden" data-testid="hero">
        <motion.img style={{ y: bgY, scale: bgScale }} src={IMAGES.hero} alt="Beautiful modern stucco home in Central Florida" className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
        <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-4xl">
            <FadeIn delay={0.1}><SectionLabel light>Central Florida Stucco Experts</SectionLabel></FadeIn>
            <h1 className="text-5xl sm:text-7xl lg:text-[5.5rem] font-heading font-black text-white leading-[0.92] mt-6 tracking-tight">
              <MaskLines lines={["Quality Stucco &", "Plaster Solutions", "Built to Last"]} delay={0.25} />
            </h1>
            <FadeIn delay={1.0}>
              <p className="text-lg sm:text-xl text-zinc-200 mt-8 max-w-2xl leading-relaxed">Licensed &amp; insured craftsmanship for homes and businesses across Orlando and Central Florida. Honest pricing, durable finishes, and reliable service — guaranteed.</p>
            </FadeIn>
            <FadeIn delay={1.2}>
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <PrimaryButton to="/contact" testid="hero-estimate-btn">Request a Free Estimate</PrimaryButton>
              </div>
            </FadeIn>
            <FadeIn delay={1.35}>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-12">
                <div className="flex items-center gap-2"><StarRating /><span className="text-white text-sm font-semibold">5.0 Rated</span></div>
                <span className="text-zinc-300 text-sm">Licensed &amp; Insured</span>
                <span className="text-zinc-300 text-sm">Free Estimates</span>
              </div>
            </FadeIn>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.8 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70">
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}><ArrowDown size={22} /></motion.div>
        </motion.div>
      </section>

      {/* TRUST STRIP */}
      <div className="bg-adn-charcoal text-white overflow-hidden py-5 border-b border-white/10" data-testid="trust-marquee">
        <div className="flex marquee-track animate-marquee">
          {[...["Licensed & Insured", "Free Estimates", "Quality Craftsmanship", "Weather-Resistant Finishes", "Honest Pricing", "Clean Job Sites", "Fast Response Times", "Residential & Commercial"], ...["Licensed & Insured", "Free Estimates", "Quality Craftsmanship", "Weather-Resistant Finishes", "Honest Pricing", "Clean Job Sites", "Fast Response Times", "Residential & Commercial"]].map((t, i) => (
            <div key={i} className="flex items-center gap-3 px-8 shrink-0">
              <span className="h-1.5 w-1.5 bg-adn-red rounded-full" />
              <span className="label-accent text-white/90 whitespace-nowrap">{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* MANIFESTO — numbered chapters */}
      <section className="py-28 bg-adn-white" data-testid="manifesto">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <Reveal><SectionLabel>Why Choose Us</SectionLabel></Reveal>
                <Reveal delay={0.1}>
                  <h2 className="text-4xl sm:text-5xl font-heading font-extrabold mt-6 leading-[1.05]">A manifesto for finishes that last</h2>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="text-zinc-600 mt-6 leading-relaxed">Four principles guide every project we take on across Central Florida.</p>
                  <div className="mt-8"><PrimaryButton to="/about" testid="manifesto-cta">Our Story</PrimaryButton></div>
                </Reveal>
              </div>
            </div>
            <div className="lg:col-span-8">
              <StaggerGroup stagger={0.12}>
                {MANIFESTO.map((m) => (
                  <StaggerItem key={m.n}>
                    <div className="group grid grid-cols-[auto_1fr] gap-6 sm:gap-10 py-8 border-t border-zinc-200 hover:border-adn-red transition-colors duration-500">
                      <div className="editorial-marquee text-5xl sm:text-7xl text-stroke group-hover:text-adn-red transition-colors duration-500">{m.n}</div>
                      <div>
                        <h3 className="text-2xl sm:text-3xl font-heading font-bold">{m.title}</h3>
                        <p className="text-zinc-600 mt-3 leading-relaxed text-lg max-w-xl">{m.body}</p>
                      </div>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerGroup>
            </div>
          </div>
        </div>
      </section>

      <MarqueeWord />

      {/* SERVICES — spotlight cards */}
      <section className="py-28 bg-adn-charcoal text-white relative overflow-hidden" data-testid="services-section">
        <div className="absolute inset-0 noise-overlay opacity-[0.03]" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <Reveal><SectionLabel>Our Services</SectionLabel></Reveal>
              <Reveal delay={0.1}><h2 className="text-4xl sm:text-5xl font-heading font-extrabold mt-6 leading-tight">Complete stucco &amp; plaster services</h2></Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link to="/services" data-testid="services-view-all" className="text-adn-red font-semibold hover:text-white flex items-center gap-2 transition-colors">View all services <ArrowRight size={18} /></Link>
            </Reveal>
          </div>
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-16" stagger={0.08}>
            {FEATURED.map((slug) => {
              const s = SERVICE_DETAILS[slug];
              return (
                <StaggerItem key={slug}>
                  <Link to={`/services/${slug}`} data-testid={`service-card-${slug}`} className="group relative block overflow-hidden h-80 bg-adn-charcoal2" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 24px 100%, 0 calc(100% - 24px))" }}>
                    <motion.img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-40 transition-opacity duration-700" whileHover={{ scale: 1.08 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute inset-0 ring-1 ring-inset ring-white/5 group-hover:ring-adn-red/60 transition-all duration-500" />
                    <div className="relative h-full flex flex-col justify-end p-7">
                      <h3 className="text-2xl font-heading font-bold">{s.title}</h3>
                      <p className="text-zinc-300 text-sm mt-2">{s.tagline}</p>
                      <span className="text-adn-red text-sm font-semibold mt-4 flex items-center gap-2 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">Learn more <ArrowRight size={16} /></span>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="py-28 bg-adn-white overflow-hidden" data-testid="about-strip">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative" y={40}>
            <div className="relative overflow-hidden">
              <img src={IMAGES.craftsman} alt="Skilled craftsman applying stucco" className="w-full h-[560px] object-cover" />
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="absolute -bottom-6 -right-6 bg-adn-red text-white p-8 hidden sm:block">
              <div className="text-5xl font-heading font-black">25+</div>
              <div className="label-accent text-white/90 mt-1">Years Experience</div>
            </motion.div>
          </Reveal>
          <div>
            <Reveal><SectionLabel>About A.D.N Stucco Inc</SectionLabel></Reveal>
            <Reveal delay={0.1}><h2 className="text-4xl sm:text-5xl font-heading font-extrabold mt-6 leading-tight">Central Florida's dedicated stucco professionals</h2></Reveal>
            <Reveal delay={0.15}><p className="text-lg text-zinc-600 mt-6 leading-relaxed">We're a family-driven team of skilled craftsmen delivering exceptional stucco and plastering services across Central Florida. From new installations to expert repairs, we treat every project with the care and attention to detail it deserves.</p></Reveal>
            <StaggerGroup className="grid grid-cols-2 gap-4 mt-8" stagger={0.07}>
              {["Attention to Detail", "Reliable Communication", "Clean Job Sites", "Customer Satisfaction"].map((t) => (
                <StaggerItem key={t} className="flex items-center gap-3 text-zinc-700 font-medium"><span className="h-2 w-2 bg-adn-red" /> {t}</StaggerItem>
              ))}
            </StaggerGroup>
            <Reveal delay={0.2}><div className="mt-10"><PrimaryButton to="/about" testid="about-strip-btn">More About Us</PrimaryButton></div></Reveal>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-28 bg-adn-off" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionLabel>Customer Reviews</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="text-4xl sm:text-5xl font-heading font-extrabold mt-6 leading-tight">Trusted by homeowners &amp; businesses</h2></Reveal>
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16" stagger={0.1}>
            {REVIEWS.slice(0, 3).map((r) => (
              <StaggerItem key={r.name}>
                <div data-testid={`review-${r.name}`} className="bg-white border border-zinc-200 p-8 h-full hover:-translate-y-1 hover:shadow-xl transition-all duration-500">
                  <StarRating />
                  <p className="text-zinc-700 mt-5 leading-relaxed italic">"{r.text}"</p>
                  <div className="mt-6 pt-6 border-t border-zinc-100">
                    <div className="font-heading font-bold">{r.name}</div>
                    <div className="text-sm text-adn-red font-semibold">{r.city}</div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal delay={0.15}>
            <div className="mt-10">
              <Link to="/reviews" data-testid="reviews-view-all" className="inline-flex items-center gap-2 border-2 border-adn-charcoal text-adn-charcoal px-8 py-4 font-semibold hover:bg-adn-charcoal hover:text-white transition-colors duration-300">Read All Reviews</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-28 bg-adn-white" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionLabel>Service Areas</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="text-4xl sm:text-5xl font-heading font-extrabold mt-6 leading-tight">Proudly serving Central Florida</h2></Reveal>
          <StaggerGroup className="flex flex-wrap gap-3 mt-12" stagger={0.03}>
            {CITIES.map((c) => (
              <StaggerItem key={c.slug}>
                <Link to={`/service-areas/${c.slug}`} data-testid={`area-${c.slug}`} className="flex items-center gap-2 border border-zinc-200 px-5 py-3 text-sm font-semibold hover:border-adn-red hover:text-adn-red hover:-translate-y-0.5 transition-all duration-300">
                  <MapPin size={16} className="text-adn-red" /> {c.name}, FL
                </Link>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
